import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';

const SmartResumeJobseeker = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  const cached = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('myProfileData') || '{}');
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    // If cache has basic identity fields, use it directly
    if (cached && (cached.firstName || cached.lastName || cached.email)) {
      setProfile(cached);
      return;
    }

    // Fallback: fetch from backend so resume is never empty
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const res = await axios.get(buildApiUrl('/api/jobseeker/profile'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: false,
        });
        const p = res?.data || {};
        sessionStorage.setItem('myProfileData', JSON.stringify(p));
        sessionStorage.setItem('myProfileTimestamp', Date.now().toString());
        setProfile(p);
      } catch (e) {
        console.error('Failed to load profile for smart resume', e);
        setProfile(cached || {});
      }
    };

    fetchProfile();
  }, [cached, user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        window.print();
      } catch (e) {
        console.error('Failed to open print dialog', e);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const data = profile || cached || {};

  const fullName = [data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ') || 'Jobseeker Name';
  const title = data.professionalTitle || data.preferredJobTitles || 'Professional Title';
  const cityCountry = [data.currentCity, data.residentCountry || data.country].filter(Boolean).join(', ') || '';

  return (
    <div style={{ margin: 0, padding: '24px', background: '#e5e7eb', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif', color: '#1f2933' }}>
      <div style={{
        maxWidth: 880,
        margin: '0 auto',
        background: '#ffffff',
        borderRadius: 18,
        boxShadow: '0 22px 50px rgba(15, 23, 42, 0.18)',
        overflow: 'hidden',
        border: '1px solid rgba(148, 163, 184, 0.25)'
      }}>
        <header style={{
          position: 'relative',
          padding: '24px 32px 20px',
          background: 'radial-gradient(circle at 0 0, #ffd9bf 0, transparent 55%), radial-gradient(circle at 100% 0, #99f6e4 0, transparent 45%), linear-gradient(120deg, #ff7a3d, #f97316, #14b8a6)',
          color: '#ffffff',
          display: 'flex',
          gap: 24,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1 }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: 24,
              background: 'rgba(255, 255, 255, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 36,
              fontWeight: 700,
              border: '1px solid rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(8px)'
            }}>
              <span>{(data.firstName || 'J').charAt(0)}{(data.lastName || 'S').charAt(0)}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px 14px' }}>
                <h1 style={{ margin: 0, fontSize: 28, letterSpacing: '0.02em' }}>{fullName}</h1>
                <span style={{
                  fontSize: 14,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'rgba(15, 23, 42, 0.22)',
                  border: '1px solid rgba(15, 23, 42, 0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.13em',
                  fontWeight: 600
                }}>
                  {title}
                </span>
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: '10px 16px', fontSize: 13, opacity: 0.95 }}>
                {cityCountry && <span>• {cityCountry}</span>}
                {data.email && <span>• {data.email}</span>}
                {(data.phone || data.phoneNumber) && <span>• {data.phone || data.phoneNumber}</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 16, textAlign: 'center' }}>
            <div>
              <img src="/FINAL_LOGO_AK.png" alt="AksharJobs Logo" style={{
                display: 'block',
                margin: '0 auto',
                height: 54,
                maxWidth: 190,
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 10px rgba(15, 23, 42, 0.45))'
              }} />
              <div style={{
                marginTop: 6,
                fontSize: 11,
                color: 'rgba(248, 250, 252, 0.92)',
                textAlign: 'center',
                maxWidth: 220,
                lineHeight: 1.4,
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                Connect. Discover. Elevate.
              </div>
            </div>
          </div>
        </header>

        <main style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.1fr) minmax(0, 1.3fr)', gap: 28, padding: '24px 32px 26px' }}>
          <section>
            <h2 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#6b7280', margin: '0 0 8px' }}>
              Profile Summary
            </h2>
            <p style={{
              fontSize: 13,
              lineHeight: 1.6,
              color: '#1f2933',
              background: '#f9fafb',
              borderRadius: 12,
              padding: '10px 12px',
              border: '1px solid #e5e7eb'
            }}>
              {data.professionalSummary || data.summary || 'No professional summary provided yet.'}
            </p>
          </section>

          <aside>
            <div style={{ borderRadius: 12, border: '1px solid #e5e7eb', padding: '10px 12px 11px', background: '#ffffff', marginBottom: 12 }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#6b7280' }}>
                Key Skills
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(() => {
                  // Normalise skills from different shapes: coreSkills (array), skills (array/string)
                  let skillsSource = [];
                  if (Array.isArray(data.coreSkills)) {
                    skillsSource = data.coreSkills;
                  } else if (Array.isArray(data.skills)) {
                    skillsSource = data.skills;
                  } else if (typeof data.skills === 'string') {
                    skillsSource = data.skills.split(/[,;]+/);
                  }
                  const skills = skillsSource.map(s => (s || '').toString().trim()).filter(Boolean).slice(0, 10);
                  if (skills.length === 0) {
                    return (
                      <li style={{
                        padding: '4px 8px',
                        borderRadius: 999,
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb'
                      }}>
                        No skills added yet
                      </li>
                    );
                  }
                  return skills.map((skill) => (
                  <li key={skill} style={{
                    padding: '4px 8px',
                    borderRadius: 999,
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb'
                  }}>
                    {skill}
                  </li>
                  ));
                })()}
              </ul>
            </div>
          </aside>
        </main>

        <footer style={{
          borderTop: '1px dashed #e5e7eb',
          padding: '10px 32px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          alignItems: 'center',
          fontSize: 10,
          color: '#6b7280',
          background: '#f9fafb'
        }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: '#111827' }}>
            Free for all AksharJobs users.
          </span>
          <span>
            Powered by AksharJobs – your job network. Learn more at{' '}
            <a href="https://aksharjobs.com" target="_blank" rel="noreferrer" style={{ color: '#e06024', textDecoration: 'none', fontWeight: 500 }}>
              aksharjobs.com
            </a>
          </span>
        </footer>
      </div>
    </div>
  );
};

export default SmartResumeJobseeker;

